import React, { useMemo, useState } from 'react';
import { Check, Copy } from 'lucide-react';
import ReactMarkdown, { type Components } from 'react-markdown';
import rehypeHighlight from 'rehype-highlight';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';
import type { ChatSource } from '../../types';
import { MermaidDiagramCard } from '../chat/MermaidDiagramCard';
import { KnowledgeCitation } from './KnowledgeCitation';
import { SourceCitation } from './SourceCitation';

interface MarkdownRendererProps {
  text: string;
  className?: string;
  sources?: ChatSource[];
  renderImageOverlay?: (image: { src?: string; alt?: string }) => React.ReactNode;
  onImageClick?: (image: { src: string; alt?: string }) => void;
}

type MarkdownNode = {
  type?: string;
  value?: string;
  url?: string;
  title?: string;
  children?: MarkdownNode[];
};

function getTextContent(node: React.ReactNode): string {
  if (typeof node === 'string' || typeof node === 'number') {
    return String(node);
  }

  if (Array.isArray(node)) {
    return node.map(getTextContent).join('');
  }

  if (React.isValidElement<{ children?: React.ReactNode }>(node)) {
    return getTextContent(node.props.children);
  }

  return '';
}

const inlineCodeClassName =
  'rounded bg-gray-100 px-1 py-0.5 font-mono text-[0.85em] text-rose-600 dark:bg-slate-900 dark:text-rose-300';

const sanitizeSchema = {
  ...defaultSchema,
  protocols: {
    ...defaultSchema.protocols,
    src: [...(defaultSchema.protocols?.src || []), 'data'],
  },
};

/** `[3]` or a grouped `[1, 4]` / `[1,2,7]` citation marker. */
const CITATION_GROUP_SOURCE = '\\[\\d+(?:\\s*,\\s*\\d+)*\\]';
const CITATION_SPLIT_PATTERN = new RegExp(`(${CITATION_GROUP_SOURCE})`);
const CITATION_GROUP_PATTERN = new RegExp(`^${CITATION_GROUP_SOURCE}$`);

function createCitationPlugin(sources: ChatSource[]) {
  // Knowledge sources have no page to open: link to a `#source-n` fragment that the `a`
  // component renders as a popover trigger instead of an external link.
  const sourceByMarker = new Map(
    sources
      .filter((source) => source.url || source.excerpt)
      .map((source) => [
        source.marker,
        { url: source.url ?? `#source-${source.marker.replace(/\D/g, '')}`, title: source.title },
      ])
  );

  return () => (tree: MarkdownNode) => {
    if (sourceByMarker.size === 0) return;
    linkCitationMarkers(tree, sourceByMarker);
  };
}

function linkCitationMarkers(
  node: MarkdownNode,
  sourceByMarker: Map<string, { url: string; title: string }>
) {
  if (!node.children || node.type === 'link') return;

  node.children = node.children.flatMap((child) => {
    if (child.type !== 'text' || !child.value) {
      linkCitationMarkers(child, sourceByMarker);
      return [child];
    }

    return child.value.split(CITATION_SPLIT_PATTERN).flatMap((part): MarkdownNode[] => {
      if (!part) return [];
      const markers = part.match(/\d+/g);
      if (!CITATION_GROUP_PATTERN.test(part) || !markers) return [{ type: 'text', value: part }];

      // A grouped marker like `[1, 4]` becomes one badge per cited source.
      const links = markers.flatMap((number): MarkdownNode[] => {
        const marker = `[${number}]`;
        const source = sourceByMarker.get(marker);
        return source
          ? [
              {
                type: 'link',
                url: source.url,
                title: source.title,
                children: [{ type: 'text', value: marker }],
              },
            ]
          : [];
      });

      return links.length === markers.length ? links : [{ type: 'text', value: part }];
    });
  });
}

function createComponents(
  sources: ChatSource[],
  renderImageOverlay?: MarkdownRendererProps['renderImageOverlay'],
  onImageClick?: MarkdownRendererProps['onImageClick']
): Components {
  return {
    h1: ({ children, ...props }) => (
      <h1 className="mb-2 mt-3 text-xl font-semibold leading-snug first:mt-0" {...props}>
        {children}
      </h1>
    ),
    h2: ({ children, ...props }) => (
      <h2 className="mb-2 mt-3 text-lg font-semibold leading-snug first:mt-0" {...props}>
        {children}
      </h2>
    ),
    h3: ({ children, ...props }) => (
      <h3 className="mb-1.5 mt-3 text-base font-semibold leading-snug first:mt-0" {...props}>
        {children}
      </h3>
    ),
    p: ({ children, ...props }) => (
      <p className="mb-2 last:mb-0" {...props}>
        {children}
      </p>
    ),
    ul: ({ children, ...props }) => (
      <ul className="mb-2 list-disc space-y-1 pl-5 last:mb-0" {...props}>
        {children}
      </ul>
    ),
    ol: ({ children, ...props }) => (
      <ol className="mb-2 list-decimal space-y-1 pl-5 last:mb-0" {...props}>
        {children}
      </ol>
    ),
    li: ({ children, ...props }) => (
      <li className="pl-1 leading-relaxed" {...props}>
        {children}
      </li>
    ),
    blockquote: ({ children, ...props }) => (
      <blockquote
        className="mb-2 border-l-2 border-gray-300 pl-3 text-gray-600 last:mb-0 dark:border-slate-600 dark:text-slate-300"
        {...props}
      >
        {children}
      </blockquote>
    ),
    a: ({ children, ...props }) => {
      const citationMatch = getTextContent(children)
        .trim()
        .match(/^\[(\d+)\]$/);
      if (citationMatch) {
        const label = citationMatch[1];
        const knowledgeSource =
          typeof props.href === 'string' && props.href.startsWith('#source-')
            ? sources.find((source) => source.marker === `[${label}]`)
            : undefined;
        if (knowledgeSource) {
          return <KnowledgeCitation label={label} source={knowledgeSource} />;
        }
        const { node: _node, href, title, ...linkProps } = props;
        void _node;
        return (
          <SourceCitation
            label={label}
            href={typeof href === 'string' ? href : '#'}
            title={typeof title === 'string' && title.trim() ? title : undefined}
            linkProps={linkProps}
          />
        );
      }

      return (
        <a
          className="font-medium text-blue-600 underline decoration-blue-300 underline-offset-2 transition-colors hover:text-blue-700 dark:text-sky-300 dark:decoration-sky-500/60 dark:hover:text-sky-200"
          target="_blank"
          rel="noopener noreferrer"
          {...props}
        >
          {children}
        </a>
      );
    },
    img: ({ src, alt, ...props }) => {
      const imageSrc = typeof src === 'string' ? src : undefined;
      const imageAlt = typeof alt === 'string' ? alt : undefined;
      const overlay = renderImageOverlay?.({ src: imageSrc, alt: imageAlt });
      const image = (
        <img
          className={`block max-h-[420px] max-w-full rounded-md object-contain ${
            onImageClick && imageSrc ? 'cursor-zoom-in transition-opacity hover:opacity-90' : ''
          }`}
          loading="lazy"
          src={src}
          alt={alt}
          {...props}
        />
      );

      return (
        <span className="relative my-2 inline-block max-w-full align-top">
          {onImageClick && imageSrc ? (
            <button
              type="button"
              className="block max-w-full text-left"
              title={imageAlt ? `Preview ${imageAlt}` : 'Preview image'}
              onClick={() => onImageClick({ src: imageSrc, alt: imageAlt })}
            >
              {image}
            </button>
          ) : (
            image
          )}
          {overlay && <span className="absolute bottom-2 right-2 z-10">{overlay}</span>}
        </span>
      );
    },
    table: ({ children, ...props }) => (
      <div className="mb-2 overflow-x-auto last:mb-0">
        <table
          className="min-w-full border-collapse overflow-hidden rounded-md border border-gray-200 text-left text-sm dark:border-slate-700"
          {...props}
        >
          {children}
        </table>
      </div>
    ),
    thead: ({ children, ...props }) => (
      <thead className="bg-gray-50 dark:bg-slate-900/70" {...props}>
        {children}
      </thead>
    ),
    th: ({ children, ...props }) => (
      <th
        className="border border-gray-200 px-2.5 py-1.5 font-semibold dark:border-slate-700"
        {...props}
      >
        {children}
      </th>
    ),
    td: ({ children, ...props }) => (
      <td
        className="border border-gray-200 px-2.5 py-1.5 align-top dark:border-slate-700"
        {...props}
      >
        {children}
      </td>
    ),
    code: ({ className, children, ...props }) => (
      <code className={className ? `${className} font-mono` : inlineCodeClassName} {...props}>
        {children}
      </code>
    ),
    pre: ({ children }) => <CodeBlock>{children}</CodeBlock>,
  };
}

function CodeBlock({ children }: { children: React.ReactNode }) {
  const [copied, setCopied] = useState(false);
  const codeElement = React.Children.toArray(children).find(React.isValidElement);
  const className = React.isValidElement<{ className?: string }>(codeElement)
    ? codeElement.props.className
    : '';
  const language = className?.match(/language-([\w-]+)/)?.[1] || 'text';
  const code = useMemo(() => getTextContent(children).replace(/\n$/, ''), [children]);

  // The agent sometimes writes the diagram as a ```mermaid fence instead of calling the tool.
  if (language === 'mermaid') {
    return (
      <MermaidDiagramCard
        diagram={{ id: 'markdown-mermaid', diagramType: code.trim().split(/\s/)[0] || 'mermaid', mermaid: code }}
      />
    );
  }

  const handleCopy = () => {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    });
  };

  return (
    <div className="mb-3 overflow-hidden rounded-md border border-slate-700 bg-slate-950 text-slate-100 last:mb-0">
      <div className="flex min-h-9 items-center justify-between gap-2 border-b border-slate-800 bg-slate-900 px-3 py-1.5">
        <span className="truncate font-mono text-xs text-slate-300">{language}</span>
        <button
          type="button"
          onClick={handleCopy}
          title="Copy code"
          className="inline-flex h-7 w-7 items-center justify-center rounded text-slate-300 transition-colors hover:bg-slate-800 hover:text-white"
        >
          {copied ? <Check size={14} className="text-green-400" /> : <Copy size={14} />}
        </button>
      </div>
      <pre className="m-0 overflow-x-auto p-3 text-[13px] leading-relaxed">{children}</pre>
    </div>
  );
}

export const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({
  text,
  className = '',
  sources = [],
  renderImageOverlay,
  onImageClick,
}) => {
  const remarkPlugins = useMemo(() => {
    if (sources.length === 0) return [remarkGfm];
    return [remarkGfm, createCitationPlugin(sources)];
  }, [sources]);
  const components = useMemo(
    () => createComponents(sources, renderImageOverlay, onImageClick),
    [sources, onImageClick, renderImageOverlay]
  );

  return (
    <div className={`markdown-renderer min-w-0 break-words ${className}`}>
      <ReactMarkdown
        remarkPlugins={remarkPlugins}
        rehypePlugins={[[rehypeSanitize, sanitizeSchema], rehypeHighlight]}
        components={components}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
};
